import { useEffect, useState } from "react";

import { listResults } from "../../api/freightAudit";
import { ReviewBadge, StatusBadge } from "../../components/Badges";
import { Button } from "../../components/Button";
import { EmptyState, ErrorNotice, Skeleton } from "../../components/Feedback";
import { Icon } from "../../components/Icon";
import { useAsync } from "../../hooks/useAsync";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { setSearchParam, useLocation } from "../../hooks/router";
import { formatCount, formatMoney, formatSignedMoney, varianceTone } from "../../lib/format";
import { REASON_DESCRIPTIONS, REASON_LABELS, REASON_ORDER, REVIEW_LABELS, reasonSummary } from "../../lib/labels";
import type {
  AuditReason,
  AuditResult,
  AuditResultDetail,
  AuditRun,
  AuditStatus,
  ResultQuery,
  ReviewState,
  SortKey,
} from "../../types/api";
import { AuditDetail, type ReviewDraft } from "./AuditDetail";

const SORTABLE: { key: SortKey; label: string; numeric?: boolean }[] = [
  { key: "status", label: "Status" },
  { key: "pro", label: "PRO" },
  { key: "invoice", label: "Invoice" },
];

function toggle<T>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

interface ExceptionTableProps {
  run: AuditRun;
  query: ResultQuery;
  onQueryChange: (query: ResultQuery) => void;
  onReviewed: () => void;
}

export function ExceptionTable({ run, query, onQueryChange, onReviewed }: ExceptionTableProps) {
  const { search } = useLocation();
  const selectedId = search.get("result");
  const [searchText, setSearchText] = useState(query.q);
  const [drafts, setDrafts] = useState<Record<string, ReviewDraft>>({});
  const debouncedSearch = useDebouncedValue(searchText, 250);

  useEffect(() => {
    if (debouncedSearch !== query.q) onQueryChange({ ...query, q: debouncedSearch, page: 1 });
  }, [debouncedSearch, query, onQueryChange]);

  const results = useAsync((signal) => listResults(run.id, query, signal), `${run.id}:${JSON.stringify(query)}`);
  const page = results.data;
  const items = page?.items ?? [];
  const summary = run.summary;
  const exceptions = summary ? summary.review + summary.hold : 0;
  const filtered = query.status.length + query.reason.length + query.review_state.length > 0 || Boolean(query.q);

  function update(change: Partial<ResultQuery>) {
    onQueryChange({ ...query, page: 1, ...change });
  }

  function sortBy(key: SortKey) {
    const current = query.sort.replace(/^-/, "");
    const descending = query.sort.startsWith("-");
    const defaultDescending = key === "status" || key === "variance";
    const next = current === key ? (descending ? key : (`-${key}` as const)) : defaultDescending ? (`-${key}` as const) : key;
    update({ sort: next });
  }

  function ariaSort(key: SortKey): "ascending" | "descending" | "none" {
    if (query.sort.replace(/^-/, "") !== key) return "none";
    return query.sort.startsWith("-") ? "descending" : "ascending";
  }

  function onSaved(saved: AuditResultDetail) {
    results.update((current) =>
      current ? { ...current, items: current.items.map((item) => (item.id === saved.id ? { ...item, ...saved } : item)) } : current,
    );
    onReviewed();
  }

  const selectedIndex = selectedId ? items.findIndex((item) => item.id === selectedId) : -1;
  const open = (item: AuditResult) => setSearchParam("result", item.id);
  const previous = selectedIndex > 0 ? items[selectedIndex - 1] : undefined;
  const next = selectedIndex >= 0 && selectedIndex < items.length - 1 ? items[selectedIndex + 1] : undefined;
  const firstRow = page ? (page.page - 1) * page.page_size + 1 : 0;
  const lastRow = page ? firstRow + page.items.length - 1 : 0;

  return (
    <section className="panel queue" aria-labelledby="queue-heading">
      <div className="queue__header">
        <h2 id="queue-heading" className="visually-hidden">
          Audit results
        </h2>
        <div className="tabs" role="tablist" aria-label="Which results">
          {(
            [
              ["exceptions", "Exceptions", exceptions],
              ["all", "All", summary?.invoice_rows ?? 0],
            ] as const
          ).map(([view, label, count]) => (
            <button
              key={view}
              type="button"
              role="tab"
              aria-selected={query.view === view}
              className={`tab${query.view === view ? " is-active" : ""}`}
              onClick={() => update({ view, status: view === "exceptions" ? query.status.filter((s) => s !== "PASS") : query.status })}
            >
              {label} <span className="tab__count">{formatCount(count)}</span>
            </button>
          ))}
        </div>
        <label className="search">
          <Icon name="search" />
          <span className="visually-hidden">Search PRO or invoice number</span>
          <input
            type="search"
            placeholder="Search PRO / invoice…"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        </label>
      </div>

      <div className="filters" role="group" aria-label="Filters">
        {(["HOLD", "REVIEW"] as AuditStatus[]).map((status) => (
          <button
            key={status}
            type="button"
            className={`chip chip--${status.toLowerCase()}${query.status.includes(status) ? " is-active" : ""}`}
            aria-pressed={query.status.includes(status)}
            onClick={() => update({ status: toggle(query.status, status) })}
          >
            {status}
          </button>
        ))}
        <span className="filters__divider" aria-hidden="true" />
        {REASON_ORDER.map((reason: AuditReason) => (
          <button
            key={reason}
            type="button"
            title={REASON_DESCRIPTIONS[reason]}
            className={`chip${query.reason.includes(reason) ? " is-active" : ""}`}
            aria-pressed={query.reason.includes(reason)}
            onClick={() => update({ reason: toggle(query.reason, reason) })}
          >
            {REASON_LABELS[reason]}
            {run.reason_counts ? <span className="chip__count">{run.reason_counts[reason]}</span> : null}
          </button>
        ))}
        <label className="filters__review">
          <span className="visually-hidden">Review decision</span>
          <select
            value={query.review_state[0] ?? ""}
            onChange={(event) => update({ review_state: event.target.value ? [event.target.value as ReviewState] : [] })}
          >
            <option value="">Any decision</option>
            {(Object.keys(REVIEW_LABELS) as ReviewState[]).map((state) => (
              <option key={state} value={state}>
                {REVIEW_LABELS[state]}
              </option>
            ))}
          </select>
        </label>
        {filtered ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setSearchText("");
              update({ status: [], reason: [], review_state: [], q: "" });
            }}
          >
            Clear filters
          </Button>
        ) : null}
      </div>

      {results.error ? <ErrorNotice error={results.error} onRetry={results.reload} /> : null}
      {!page && results.loading ? <Skeleton lines={6} label="Loading results" /> : null}

      {page && page.total === 0 ? (
        filtered ? (
          <EmptyState title="No results match these filters." />
        ) : query.view === "exceptions" ? (
          <EmptyState title="No exceptions — every invoice passed.">
            <p>Every invoice matched its quote within tolerance. Nothing needs review.</p>
          </EmptyState>
        ) : (
          <EmptyState title="This audit has no invoice rows." />
        )
      ) : null}

      {page && page.total > 0 ? (
        <div className="table-wrap" aria-busy={results.loading}>
          <table className="results-table">
            <caption className="visually-hidden">
              {query.view === "exceptions" ? "Exceptions" : "All invoice rows"}, {formatCount(page.total)} rows
            </caption>
            <thead>
              <tr>
                {SORTABLE.map((column) => (
                  <th key={column.key} scope="col" aria-sort={ariaSort(column.key)}>
                    <button type="button" className="sort" onClick={() => sortBy(column.key)}>
                      {column.label}
                    </button>
                  </th>
                ))}
                <th scope="col" className="numeric">
                  Quote
                </th>
                <th scope="col" className="numeric">
                  Bill
                </th>
                <th scope="col" className="numeric" aria-sort={ariaSort("variance")}>
                  <button type="button" className="sort" onClick={() => sortBy("variance")}>
                    Δ
                    <span className="visually-hidden"> (variance)</span>
                  </button>
                </th>
                <th scope="col" aria-sort={ariaSort("reason")}>
                  <button type="button" className="sort" onClick={() => sortBy("reason")}>
                    Reason
                  </button>
                </th>
                <th scope="col">Review</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className={`${item.id === selectedId ? "is-selected" : ""}${drafts[item.id] ? " has-draft" : ""}`}
                  onClick={() => open(item)}
                >
                  <td>
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="mono">
                    <button
                      type="button"
                      className="row-link"
                      onClick={(event) => {
                        event.stopPropagation();
                        open(item);
                      }}
                      aria-label={`Open invoice ${item.invoice_number || "(blank)"}, PRO ${item.pro_number || "(blank)"}`}
                    >
                      {item.pro_number || <span className="muted">(blank)</span>}
                    </button>
                  </td>
                  <td className="mono">{item.invoice_number || <span className="muted">(blank)</span>}</td>
                  <td className="numeric">{formatMoney(item.quoted_total)}</td>
                  <td className="numeric">{formatMoney(item.billed_total)}</td>
                  <td className={`numeric variance variance--${varianceTone(item.total_variance)}`}>
                    {formatSignedMoney(item.total_variance)}
                  </td>
                  <td>{reasonSummary(item.reason_codes)}</td>
                  <td>
                    <ReviewBadge state={item.review_state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {page && page.total > page.page_size ? (
        <nav className="pager" aria-label="Pages">
          <span>
            {formatCount(firstRow)}–{formatCount(lastRow)} of {formatCount(page.total)}
          </span>
          <Button size="sm" icon="chevron-left" disabled={page.page <= 1} onClick={() => onQueryChange({ ...query, page: page.page - 1 })}>
            Previous
          </Button>
          <Button
            size="sm"
            iconAfter="chevron-right"
            disabled={lastRow >= page.total}
            onClick={() => onQueryChange({ ...query, page: page.page + 1 })}
          >
            Next
          </Button>
        </nav>
      ) : null}

      {selectedId ? (
        <AuditDetail
          key={selectedId}
          auditId={run.id}
          resultId={selectedId}
          position={selectedIndex >= 0 && page ? { index: firstRow - 1 + selectedIndex, total: page.total } : null}
          draft={drafts[selectedId]}
          onDraftChange={(draft) =>
            setDrafts((current) => {
              const nextDrafts = { ...current };
              if (draft) nextDrafts[selectedId] = draft;
              else delete nextDrafts[selectedId];
              return nextDrafts;
            })
          }
          onSaved={onSaved}
          onPrevious={previous ? () => open(previous) : null}
          onNext={next ? () => open(next) : null}
          onClose={() => setSearchParam("result", null)}
        />
      ) : null}
    </section>
  );
}
